/**
 * EditorBridge — the only module that touches the canvas iframe's DOM.
 *
 * The editor document (server-built, same-origin srcdoc, no scripts) carries:
 * - comment-pair field anchors:  <!-- apexos-field:KEY --> … <!-- /apexos-field:KEY -->
 * - restorable component placeholders: [data-apexos-restore="<escaped marker>"]
 * - chrome bounds: <!-- apexos-chrome vN --> … <!-- /apexos-chrome -->
 *
 * Serialization contract (mirrors the server's fail-closed validation):
 * - only content between field anchors is ever read back;
 * - placeholder elements are replaced by their verbatim marker text;
 * - every editor artifact (data-apexos-*, contenteditable, editor classes)
 *   is stripped, then stripEditorArtifacts() runs as belt-and-braces.
 *
 * Kept deliberately framework-free so a future cross-origin/postMessage
 * implementation can swap in behind the same interface.
 */
import DOMPurify from 'dompurify';

import type { EditorFieldKey, EditorRegion, EditorRegionName, EditorSelection } from '../../../../types/websiteBuilder';
import {
  EDITOR_ARTIFACT_ATTR,
  decodeRestoreAttr,
  restoreDynamicAttrs,
  stripEditorArtifacts,
  toSelection,
  type ElementDescriptor,
} from './visualEditorLogic';

const EDITOR_CSS = `
  [data-apexos-hover]{outline:2px dashed #7c3aed !important;outline-offset:2px;cursor:pointer;}
  [data-apexos-selected]{outline:2px solid #7c3aed !important;outline-offset:2px;}
  [contenteditable="true"]{outline:2px solid #16a34a !important;outline-offset:2px;min-height:1em;}
  a, button {cursor: pointer;}
`;

const SECTION_TAGS = new Set(['section', 'article', 'aside']);

export interface BridgeCallbacks {
  onSelect: (selection: EditorSelection | null, elementPath: number[]) => void;
  onTextChanged: () => void;
  onBeforeChange?: () => void;
}

export class EditorBridge {
  private iframe: HTMLIFrameElement;

  private callbacks: BridgeCallbacks;

  private selected: HTMLElement | null = null;

  private editing: HTMLElement | null = null;

  /** Anchor ids inside the element when the current inline edit began. */
  private editingAnchors: string[] = [];

  private regions: EditorRegion[] = [];

  constructor(iframe: HTMLIFrameElement, callbacks: BridgeCallbacks) {
    this.iframe = iframe;
    this.callbacks = callbacks;
  }

  /** Region metadata from the server drives what is editable and its scope. */
  setRegions(regions: EditorRegion[]): void {
    this.regions = regions || [];
  }

  regionFor(name: EditorRegionName | undefined): EditorRegion | undefined {
    return name ? this.regions.find((r) => r.name === name) : undefined;
  }

  private get doc(): Document | null {
    return this.iframe.contentDocument;
  }

  /** Load a fresh editor document and (re)attach handlers. */
  load(html: string): Promise<void> {
    return new Promise((resolve) => {
      const onLoad = () => {
        this.iframe.removeEventListener('load', onLoad);
        this.selected = null;
        this.editing = null;
        this.prepareDocument();
        resolve();
      };
      this.iframe.addEventListener('load', onLoad);
      this.iframe.srcdoc = html;
    });
  }

  private prepareDocument(): void {
    const doc = this.doc;
    if (!doc) return;
    const style = doc.createElement('style');
    style.id = 'apexos-editor-css';
    style.textContent = EDITOR_CSS;
    doc.head.appendChild(style);

    doc.addEventListener('click', this.handleClick, true);
    doc.addEventListener('mouseover', this.handleHover, true);
    doc.addEventListener('mouseout', this.handleHoverOut, true);
    doc.addEventListener('input', this.handleInput, true);
    doc.addEventListener('paste', this.handlePaste, true);
  }

  destroy(): void {
    const doc = this.doc;
    if (!doc) return;
    doc.removeEventListener('click', this.handleClick, true);
    doc.removeEventListener('mouseover', this.handleHover, true);
    doc.removeEventListener('mouseout', this.handleHoverOut, true);
    doc.removeEventListener('input', this.handleInput, true);
    doc.removeEventListener('paste', this.handlePaste, true);
  }

  // ------------------------------------------------------------------
  // Field anchors
  // ------------------------------------------------------------------

  /** Every anchored span, keyed "field#index" (a layout may substitute the
   * same field more than once, so anchors carry an occurrence index). */
  private fieldAnchors(): Map<string, [Comment, Comment]> {
    const result = new Map<string, [Comment, Comment]>();
    const doc = this.doc;
    if (!doc) return result;
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_COMMENT);
    const starts = new Map<string, Comment>();
    let node = walker.nextNode();
    while (node) {
      const text = (node.nodeValue || '').trim();
      const startMatch = /^apexos-field:(\w+)#(\d+)$/.exec(text);
      const endMatch = /^\/apexos-field:(\w+)#(\d+)$/.exec(text);
      if (startMatch) starts.set(`${startMatch[1]}#${startMatch[2]}`, node as Comment);
      if (endMatch) {
        const id = `${endMatch[1]}#${endMatch[2]}`;
        const start = starts.get(id);
        if (start) result.set(id, [start, node as Comment]);
      }
      node = walker.nextNode();
    }
    return result;
  }

  /** Every comment node inside *el*, in document order. */
  private commentsWithin(el: HTMLElement): Comment[] {
    const doc = this.doc;
    if (!doc) return [];
    const walker = doc.createTreeWalker(el, NodeFilter.SHOW_COMMENT);
    const found: Comment[] = [];
    let node = walker.nextNode();
    while (node) {
      found.push(node as Comment);
      node = walker.nextNode();
    }
    return found;
  }

  /** The anchor pairs living wholly inside *el*, in document order.
   *
   * Anchors are COMMENT nodes, so any write that replaces an element's
   * children deletes them silently. The server rebuilds a stored region by
   * turning each anchor pair back into its {{placeholder}} — lose one and
   * the save is refused ("the edit would drop the per-page value(s) …")
   * AND the field itself no longer serializes. Every text-mutating op below
   * goes through here rather than touching textContent directly.
   */
  private anchorPairsWithin(el: HTMLElement): Array<[string, [Comment, Comment]]> {
    return [...this.fieldAnchors()].filter(([, [start, end]]) => el.contains(start) && el.contains(end));
  }

  /** The slice of *el* a flat text edit may rewrite: the inside of its first
   * anchor pair when it has one (the anchors, and any static copy the layout
   * puts around them, must survive), else the whole element. */
  private textRange(el: HTMLElement): Range | null {
    const doc = this.doc;
    if (!doc) return null;
    const range = doc.createRange();
    const pair = this.anchorPairsWithin(el)[0];
    if (pair) {
      range.setStartAfter(pair[1][0]);
      range.setEndBefore(pair[1][1]);
    } else {
      range.selectNodeContents(el);
    }
    return range;
  }

  /** The first anchored span for a field — what field-level edits act on. */
  private fieldSpan(key: EditorFieldKey): [Comment, Comment] | undefined {
    const anchors = this.fieldAnchors();
    const ids = [...anchors.keys()]
      .filter((id) => id.startsWith(`${key}#`))
      .sort((x, y) => Number(x.split('#')[1]) - Number(y.split('#')[1]));
    return ids.length ? anchors.get(ids[0]) : undefined;
  }

  /** Map of region name -> [startComment, endComment]. */
  private regionAnchors(): Map<EditorRegionName, [Comment, Comment]> {
    const result = new Map<EditorRegionName, [Comment, Comment]>();
    const doc = this.doc;
    if (!doc) return result;
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_COMMENT);
    const starts = new Map<string, Comment>();
    let node = walker.nextNode();
    while (node) {
      const text = (node.nodeValue || '').trim();
      const open = /^apexos-region:(\w+)$/.exec(text);
      const close = /^\/apexos-region:(\w+)$/.exec(text);
      if (open) starts.set(open[1], node as Comment);
      if (close) {
        const start = starts.get(close[1]);
        if (start) result.set(close[1] as EditorRegionName, [start, node as Comment]);
      }
      node = walker.nextNode();
    }
    return result;
  }

  /** Which stored region a node sits inside, if any. */
  private regionForNode(node: Node): EditorRegionName | undefined {
    const doc = this.doc;
    if (!doc) return undefined;
    for (const [name, [start, end]] of this.regionAnchors()) {
      const range = doc.createRange();
      range.setStartAfter(start);
      range.setEndBefore(end);
      if (range.intersectsNode(node)) return name;
    }
    return undefined;
  }

  /** The field a node sits inside (scan siblings back to an anchor comment). */
  private fieldKeyForNode(node: Node): EditorFieldKey | undefined {
    for (const [id, [start, end]] of this.fieldAnchors()) {
      const range = this.doc!.createRange();
      range.setStartAfter(start);
      range.setEndBefore(end);
      if (range.intersectsNode(node)) return id.split('#')[0] as EditorFieldKey;
    }
    return undefined;
  }

  private isInChrome(el: HTMLElement): boolean {
    // Walk preceding siblings/ancestors looking for the chrome comment pair.
    let node: Node | null = el;
    while (node && node !== this.doc?.body) {
      let sibling: Node | null = node.previousSibling;
      while (sibling) {
        if (sibling.nodeType === Node.COMMENT_NODE) {
          const text = (sibling.nodeValue || '').trim();
          if (text === '/apexos-chrome') return false;
          if (/^apexos-chrome v\d+$/.test(text)) return true;
        }
        sibling = sibling.previousSibling;
      }
      node = node.parentNode;
    }
    return false;
  }

  private isInHeroRegion(el: HTMLElement): boolean {
    let node: Node | null = el;
    while (node && node !== this.doc?.body) {
      let sibling: Node | null = node.previousSibling;
      while (sibling) {
        if (sibling.nodeType === Node.COMMENT_NODE) {
          const text = (sibling.nodeValue || '').trim();
          if (text === '/apexos-region:hero') return false;
          if (text === 'apexos-region:hero') return true;
        }
        sibling = sibling.previousSibling;
      }
      node = node.parentNode;
    }
    return false;
  }

  // ------------------------------------------------------------------
  // Selection
  // ------------------------------------------------------------------

  private describe(el: HTMLElement): ElementDescriptor {
    const carrier = el.closest<HTMLElement>('[data-apexos-restore]');
    const fieldKey = this.fieldKeyForNode(el);
    const regionName = this.regionForNode(el);
    return {
      regionName,
      regionEditable: !!this.regionFor(regionName)?.editable,
      tag: el.tagName.toLowerCase(),
      classes: Array.from(el.classList),
      inlineStyle: el.getAttribute('style') || '',
      href: el.getAttribute('href') ?? undefined,
      src: el.getAttribute('src') ?? undefined,
      alt: el.getAttribute('alt') ?? undefined,
      // The sidebar seeds its textarea from this and writes it back through
      // setText(), so it must be the same slice setText() rewrites — and it
      // must never be truncated, or committing an untouched long paragraph
      // would silently amputate it.
      text: (this.textRange(el)?.toString() ?? '').trim(),
      restoreMarker: carrier ? decodeRestoreAttr(carrier.getAttribute('data-apexos-restore') || '') : undefined,
      lockedKind: carrier?.getAttribute('data-apexos-kind') ?? undefined,
      slot: (carrier || el).getAttribute('data-apexos-slot') ?? undefined,
      fieldKey,
      inChrome: this.isInChrome(el),
      inHeroRegion: this.isInHeroRegion(el),
      isSectionRoot: SECTION_TAGS.has(el.tagName.toLowerCase()),
    };
  }

  /** Stable path of child indices from body to the element (survives a
   * same-document re-render only if structure is unchanged — used for
   * re-selection after save). */
  private pathOf(el: HTMLElement): number[] {
    const path: number[] = [];
    let node: HTMLElement | null = el;
    while (node && node !== this.doc?.body) {
      const parent: HTMLElement | null = node.parentElement;
      if (!parent) break;
      path.unshift(Array.prototype.indexOf.call(parent.children, node));
      node = parent;
    }
    return path;
  }

  elementAtPath(path: number[]): HTMLElement | null {
    let node: Element | null | undefined = this.doc?.body;
    for (const index of path) {
      node = node?.children[index];
      if (!node) return null;
    }
    return (node as HTMLElement) || null;
  }

  private handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    // Never navigate inside the canvas.
    event.preventDefault();
    if (this.editing && (target === this.editing || this.editing.contains(target))) {
      return; // clicks inside an active inline edit place the caret
    }
    event.stopPropagation();
    this.endInlineEdit();

    // Prefer the locked-component carrier, else the most specific
    // interesting ancestor: button/link > img > text tag > section.
    const carrier = target.closest<HTMLElement>('[data-apexos-restore]');
    const clickable =
      carrier ||
      target.closest<HTMLElement>('a, button') ||
      (target.tagName === 'IMG' ? target : null) ||
      target.closest<HTMLElement>('h1,h2,h3,h4,h5,h6,p,li,blockquote,figcaption,td,th,dt,dd') ||
      target.closest<HTMLElement>('section,article,aside') ||
      target;

    this.markSelected(clickable);
    this.callbacks.onSelect(toSelection(this.describe(clickable)), this.pathOf(clickable));
  };

  private handleHover = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target || this.editing) return;
    target.setAttribute('data-apexos-hover', '');
  };

  private handleHoverOut = (event: MouseEvent): void => {
    (event.target as HTMLElement | null)?.removeAttribute('data-apexos-hover');
  };

  private handleInput = (): void => {
    if (this.editing) this.callbacks.onTextChanged();
  };

  private handlePaste = (event: ClipboardEvent): void => {
    if (!this.editing) return;
    // Paste as plain text — clipboard HTML is the #1 source of garbage markup.
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') ?? '';
    const selection = this.doc?.getSelection();
    if (!selection || !selection.rangeCount) return;
    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(this.doc!.createTextNode(text));
    selection.collapseToEnd();
    this.callbacks.onTextChanged();
  };

  private markSelected(el: HTMLElement): void {
    this.selected?.removeAttribute('data-apexos-selected');
    this.selected = el;
    el.setAttribute('data-apexos-selected', '');
    el.removeAttribute('data-apexos-hover');
  }

  clearSelection(): void {
    this.selected?.removeAttribute('data-apexos-selected');
    this.selected = null;
    this.endInlineEdit();
  }

  // ------------------------------------------------------------------
  // Ops (all run on the currently selected element)
  // ------------------------------------------------------------------

  get selectedElement(): HTMLElement | null {
    return this.selected;
  }

  beginInlineEdit(): void {
    if (!this.selected) return;
    this.callbacks.onBeforeChange?.();
    this.editing = this.selected;
    this.editingAnchors = this.anchorPairsWithin(this.selected).map(([id]) => id);
    this.editing.setAttribute('contenteditable', 'true');
    this.editing.focus();
  }

  endInlineEdit(): void {
    const el = this.editing;
    if (!el) return;
    el.removeAttribute('contenteditable');
    this.editing = null;
    this.restoreLostAnchors(el, this.editingAnchors);
    this.editingAnchors = [];
  }

  /** Put back any anchor pair the inline edit destroyed.
   *
   * Selecting a whole field and retyping (or pasting over it) takes the
   * anchor comments with the old text. The text now in the element IS the
   * field's new value, so re-wrapping what is left restores the placeholder
   * in the right place. A layout that substitutes several fields into one
   * element can only get its first one back that way; the rest are restored
   * as empty pairs, because the placeholder is what the other pages depend
   * on and an empty value here beats a refused save.
   */
  private restoreLostAnchors(el: HTMLElement, ids: string[]): void {
    const doc = this.doc;
    if (!doc || !ids.length) return;
    const present = new Set(this.anchorPairsWithin(el).map(([id]) => id));
    const lost = ids.filter((id) => !present.has(id));
    if (!lost.length) return;
    // A half-destroyed pair leaves an orphan comment behind; drop it so the
    // pair we re-emit is the only one for that id.
    this.commentsWithin(el).forEach((comment) => {
      const orphan = /^\/?apexos-field:(\w+#\d+)$/.exec((comment.nodeValue || '').trim());
      if (orphan && lost.includes(orphan[1])) comment.remove();
    });
    const [primary, ...rest] = lost;
    el.insertBefore(doc.createComment(` apexos-field:${primary} `), el.firstChild);
    el.appendChild(doc.createComment(` /apexos-field:${primary} `));
    rest.forEach((id) => {
      el.appendChild(doc.createComment(` apexos-field:${id} `));
      el.appendChild(doc.createComment(` /apexos-field:${id} `));
    });
  }

  /** Replace the selection's editable text, keeping its field anchors.
   *
   * `textContent = …` would drop every child node, anchors included; the
   * range write below rewrites only what sits BETWEEN them, so the region
   * still round-trips to its {{placeholder}}. What it rewrites is exactly
   * what `describe()` reported as the element's text, so the sidebar's
   * textarea and this write always agree.
   */
  setText(text: string): void {
    this.callbacks.onBeforeChange?.();
    const el = this.selected;
    const doc = this.doc;
    if (!el || !doc) return;
    const range = this.textRange(el);
    if (!range) return;
    range.deleteContents();
    range.insertNode(doc.createTextNode(text));
  }

  setAttr(name: string, value: string | null): void {
    this.callbacks.onBeforeChange?.();
    if (!this.selected) return;
    if (value === null) this.selected.removeAttribute(name);
    else this.selected.setAttribute(name, value);
  }

  setClasses(classes: string[]): void {
    this.callbacks.onBeforeChange?.();
    if (!this.selected) return;
    if (classes.length) this.selected.setAttribute('class', classes.join(' '));
    else this.selected.removeAttribute('class');
  }

  /** Set a style property on the selection. If the element's style was
   * display-resolved from a dynamic image marker (data-apexos-restore-style),
   * the user's explicit restyle displaces that binding: the restore attr is
   * dropped (so the current, resolved style serializes as-is) and the
   * displaced marker(s) are returned for the shell's removed_markers list —
   * otherwise serialization would silently revert the user's change. */
  setStyleProperty(prop: string, value: string): string[] {
    this.callbacks.onBeforeChange?.();
    if (!this.selected) return [];
    const displaced: string[] = [];
    const restore = this.selected.getAttribute('data-apexos-restore-style');
    if (restore) {
      const original = decodeRestoreAttr(restore);
      displaced.push(...(original.match(/<!--\s*APEXOS:[^>]*?-->/g) ?? []));
      this.selected.removeAttribute('data-apexos-restore-style');
    }
    if (value) this.selected.style.setProperty(prop, value);
    else this.selected.style.removeProperty(prop);
    return displaced;
  }

  swapImage(url: string, alt: string): void {
    this.callbacks.onBeforeChange?.();
    if (!this.selected || this.selected.tagName !== 'IMG') return;
    this.selected.setAttribute('src', url);
    this.selected.setAttribute('alt', alt);
  }

  /** Section ops operate on the selected element itself. */
  moveSection(direction: -1 | 1): void {
    this.callbacks.onBeforeChange?.();
    const el = this.selected;
    if (!el || !el.parentElement) return;
    const sibling = direction === -1 ? el.previousElementSibling : el.nextElementSibling;
    if (!sibling) return;
    if (direction === -1) el.parentElement.insertBefore(el, sibling);
    else el.parentElement.insertBefore(sibling, el);
  }

  duplicateSection(): void {
    this.callbacks.onBeforeChange?.();
    const el = this.selected;
    if (!el || !el.parentElement) return;
    const clone = el.cloneNode(true) as HTMLElement;
    // A duplicate must not duplicate managed components (marker parity would
    // reject the save) — strip restore-carrying placeholders from the clone.
    clone.querySelectorAll('[data-apexos-restore]').forEach((node) => node.remove());
    // Same reasoning for field anchors: a second copy would re-tokenise into
    // a second {{placeholder}} the stored source never had. The duplicate
    // keeps the text it was copied with, as static layout copy.
    this.commentsWithin(clone).forEach((comment) => {
      if (/^\/?apexos-field:\w+#\d+$/.test((comment.nodeValue || '').trim())) comment.remove();
    });
    clone.removeAttribute('data-apexos-selected');
    // after(), not insertBefore(nextElementSibling): a region's bounds are
    // COMMENT nodes, so duplicating the last section in a layout would place
    // the copy past the closing comment, outside the region that gets
    // serialized — and the duplicate would vanish on save.
    el.after(clone);
  }

  deleteSelected(): string[] {
    this.callbacks.onBeforeChange?.();
    const el = this.selected;
    if (!el) return [];
    const removedMarkers = Array.from(el.querySelectorAll('[data-apexos-restore]'))
      .map((node) => decodeRestoreAttr(node.getAttribute('data-apexos-restore') || ''))
      .filter(Boolean);
    const own = el.getAttribute('data-apexos-restore');
    if (own) removedMarkers.push(decodeRestoreAttr(own));
    el.remove();
    this.selected = null;
    return removedMarkers;
  }

  /** Preserve the complete canvas, including shared sources and component markers. */
  snapshotCanvas(): string {
    const clone = this.doc?.body.cloneNode(true) as HTMLElement | undefined;
    if (!clone) return '';
    clone.querySelectorAll('[data-apexos-selected], [data-apexos-hover], [contenteditable]').forEach(el => {
      el.removeAttribute('data-apexos-selected');
      el.removeAttribute('data-apexos-hover');
      el.removeAttribute('contenteditable');
    });
    return clone.innerHTML;
  }

  restoreCanvas(html: string): void {
    if (!this.doc) return;
    this.selected = null;
    this.editing = null;
    this.editingAnchors = [];
    this.doc.body.innerHTML = html;
    this.callbacks.onSelect(null, []);
  }

  /** Snapshot/restore one field's DOM for the undo stack. */
  snapshotField(key: EditorFieldKey): string | null {
    return this.rawFieldHtml(key);
  }

  restoreField(key: EditorFieldKey, rawHtml: string): void {
    const anchors = this.fieldSpan(key);
    const doc = this.doc;
    if (!anchors || !doc) return;
    const [start, end] = anchors;
    const range = doc.createRange();
    range.setStartAfter(start);
    range.setEndBefore(end);
    range.deleteContents();
    const template = doc.createElement('template');
    template.innerHTML = rawHtml;
    range.insertNode(template.content);
    this.clearSelection();
  }

  private rawFieldHtml(key: EditorFieldKey): string | null {
    const anchors = this.fieldSpan(key);
    const doc = this.doc;
    if (!anchors || !doc) return null;
    const [start, end] = anchors;
    const parts: string[] = [];
    let node: Node | null = start.nextSibling;
    while (node && node !== end) {
      parts.push(
        node.nodeType === Node.ELEMENT_NODE
          ? (node as HTMLElement).outerHTML
          : node.nodeType === Node.COMMENT_NODE
            ? `<!--${node.nodeValue || ''}-->`
            : (node.nodeValue || '').replace(/&/g, '&amp;').replace(/</g, '&lt;')
      );
      node = node.nextSibling;
    }
    return parts.join('');
  }

  // ------------------------------------------------------------------
  // Serialization
  // ------------------------------------------------------------------

  /** Serialize one field back to source HTML (marker restoration + cleanup). */
  serializeField(key: EditorFieldKey): string | null {
    const anchors = this.fieldSpan(key);
    const doc = this.doc;
    if (!anchors || !doc) return null;
    const [start, end] = anchors;
    const range = doc.createRange();
    range.setStartAfter(start);
    range.setEndBefore(end);
    const fragment = range.cloneContents();
    const holder = doc.createElement('div');
    holder.appendChild(fragment);
    return this.cleanForSave(holder);
  }

  /** Marker restoration + artifact stripping + sanitisation, shared by
   * field and region serialization so they can never diverge. */
  private cleanForSave(holder: HTMLElement): string {
    const doc = this.doc!;

    // Attribute-carried originals (style/src/href that held an image marker)
    // must cross the sanitiser as inert tokens: DOMPurify drops any attribute
    // whose value contains "<!--" as an mXSS risk, which would silently save
    // this page's resolved photo URL into shared source.
    const attrTokens: string[] = [];
    holder
      .querySelectorAll('[data-apexos-restore-style],[data-apexos-restore-src],[data-apexos-restore-href]')
      .forEach((node) => {
        const el = node as HTMLElement;
        (['style', 'src', 'href'] as const).forEach((attr) => {
          const carrier = el.getAttribute(`data-apexos-restore-${attr}`);
          if (carrier === null) return;
          el.setAttribute(attr, `__APEXOS_RESTORE_${attrTokens.length}__`);
          attrTokens.push(decodeRestoreAttr(carrier));
          el.removeAttribute(`data-apexos-restore-${attr}`);
        });
      });

    // Restore managed components to their verbatim marker text.
    holder.querySelectorAll('[data-apexos-restore]').forEach((node) => {
      const marker = decodeRestoreAttr(node.getAttribute('data-apexos-restore') || '');
      node.replaceWith(doc.createComment(marker.replace(/^<!--/, '').replace(/-->$/, '')));
    });

    // Strip editor state attributes/classes.
    holder.querySelectorAll('*').forEach((node) => {
      const el = node as HTMLElement;
      Array.from(el.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-apexos-restore-')) return; // consumed by restoreDynamicAttrs
        // Only the editor's own attributes — the site's data-apexos-quote-open
        // hook must survive, or the quote drawer stops working.
        if (
          EDITOR_ARTIFACT_ATTR.test(attr.name) ||
          attr.name === 'contenteditable' ||
          attr.name === 'spellcheck'
        ) {
          el.removeAttribute(attr.name);
        }
      });
      Array.from(el.classList)
        .filter((c) => c.startsWith('apexos-editor-'))
        .forEach((c) => el.classList.remove(c));
      if (!el.classList.length) el.removeAttribute('class');
    });

    const sanitized = DOMPurify.sanitize(holder.innerHTML, {
      ALLOWED_ATTR: ['class', 'style', 'href', 'src', 'alt', 'id', 'target', 'rel', 'width', 'height', 'loading', 'aria-label', 'role', 'viewBox', 'fill', 'stroke', 'stroke-width', 'd', 'points', 'cx', 'cy', 'r', 'x', 'y', 'rx', 'x1', 'x2', 'y1', 'y2', 'xmlns', 'stroke-linecap', 'stroke-linejoin', 'data-site-config', 'data-apexos-restore-style', 'data-apexos-restore-src', 'data-apexos-restore-href'],
      // '#comment' is REQUIRED: DOMPurify strips comments by default, and our
      // field anchors (<!-- apexos-field:key#n -->) and restored component
      // markers (<!-- APEXOS:FORM -->) are comments. Without this the server
      // sees every placeholder and component as deleted and refuses the save.
      ADD_TAGS: ['#comment', 'svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'g'],
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
      ALLOW_DATA_ATTR: true,
    });
    // Put the attribute originals back now that sanitisation is done, then
    // strip any leftover editor artifacts. restoreDynamicAttrs still runs as
    // a safety net for anything that reached here with its carrier intact.
    let out = restoreDynamicAttrs(sanitized);
    attrTokens.forEach((original, index) => {
      out = out.split(`__APEXOS_RESTORE_${index}__`).join(original.replace(/"/g, '&quot;'));
    });
    return stripEditorArtifacts(out);
  }

  /** Serialize one stored region back to source.
   *
   * Field anchors are deliberately LEFT IN: the server turns them back into
   * {{placeholders}} by exact index, which is what stops one page's content
   * being baked into a layout its siblings share. */
  serializeRegion(name: EditorRegionName): string | null {
    const anchors = this.regionAnchors().get(name);
    const doc = this.doc;
    if (!anchors || !doc) return null;
    const [start, end] = anchors;
    const range = doc.createRange();
    range.setStartAfter(start);
    range.setEndBefore(end);
    const holder = doc.createElement('div');
    holder.appendChild(range.cloneContents());
    return this.cleanForSave(holder);
  }

  serializeScalar(key: EditorFieldKey): string | null {
    const raw = this.serializeField(key);
    if (raw === null) return null;
    const holder = this.doc!.createElement('div');
    holder.innerHTML = raw;
    return (holder.textContent || '').trim();
  }
}
